import type { Client } from './client';
import type { InvoiceType } from './invoice';
import type { Location } from './location';
import type { Manager } from './manager';
import type { ISODate, ISODateString, Nullable, UUID } from './common';
import type { JobCardFields } from '../schemas/jobCardFields';
import type { ExpenseSortField, SortDirection } from '../utils/expenseSort';
import type { JobBillingState } from '../utils/invoiceLifecycle';

export const JOB_STATUSES = [
  'quote_requested',
  'estimate_sent',
  'not_awarded',
  'awarded',
  'in_progress',
  'completed',
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

/** @deprecated Replaced by JOB_CARD_STATUSES + JOB_INVOICE_STATUSES. */
export const BILLING_STATUSES = [
  'not_started',
  'job_card_started',
  'job_card_ready',
  'invoice_ready',
  'invoice_sent',
  'paid',
] as const;
/** @deprecated Replaced by JobCardStatus + JobInvoiceStatus. */
export type BillingStatus = (typeof BILLING_STATUSES)[number];

export const JOB_CARD_STATUSES = ['not_started', 'drafting', 'ready'] as const;
export type JobCardStatus = (typeof JOB_CARD_STATUSES)[number];

export const JOB_INVOICE_STATUSES = [
  'not_invoiced',
  'needs_review',
  'ready_to_send',
  'invoice_sent',
  'partially_paid',
  'paid',
] as const;
export type JobInvoiceStatus = (typeof JOB_INVOICE_STATUSES)[number];

export const JOB_DISPOSITIONS = ['awarded', 'not_awarded'] as const;
export type JobDisposition = (typeof JOB_DISPOSITIONS)[number];

export const BILLING_TYPES = ['one_time', 'monthly', 'quarterly'] as const;
export type BillingType = (typeof BILLING_TYPES)[number];

export const HIGHLIGHT_COLORS = ['yellow', 'red', 'green', 'blue', 'purple', 'orange'] as const;
export type HighlightColor = (typeof HIGHLIGHT_COLORS)[number];

export interface Job {
  id: UUID;
  jobCode: string;
  clientId: UUID;
  locationId: UUID;
  managerId: Nullable<UUID>;
  projectType: string;
  customProjectType?: Nullable<string>;
  description?: Nullable<string>;
  serviceAddressNote?: Nullable<string>;
  billingAddressLabel?: Nullable<string>;
  billingAddressLine1?: Nullable<string>;
  billingAddressLine2?: Nullable<string>;
  billingCity?: Nullable<string>;
  billingState?: Nullable<string>;
  billingZip?: Nullable<string>;
  status: JobStatus;
  /** @deprecated Replaced by jobCardStatus + invoiceStatus. */
  billingStatus?: Nullable<BillingStatus>;
  jobCardStatus: JobCardStatus;
  invoiceStatus: JobInvoiceStatus;
  invoicePaidDate?: Nullable<ISODate>;
  disposition?: Nullable<JobDisposition>;
  noticeDate?: Nullable<ISODate>;
  estimateSendDate?: Nullable<ISODate>;
  startDate?: Nullable<ISODate>;
  completedDate?: Nullable<ISODate>;
  onedrivePath?: Nullable<string>;
  jobCardPath?: Nullable<string>;
  jobCardFields?: Nullable<JobCardFields>;
  invoiceType?: Nullable<InvoiceType>;
  invoiceNumber?: Nullable<string>;
  /** @deprecated Edit UI removed. Read-only on legacy surfaces. */
  billingType?: Nullable<BillingType>;
  /** @deprecated Edit UI removed. Read-only on legacy surfaces. */
  billingRate?: Nullable<string>;
  lastInvoicedDate?: Nullable<ISODate>;
  activeBreakdownVersionId?: Nullable<UUID>;
  breakdownModifiedSinceVersion: boolean;
  isArchived: boolean;
  highlightColor?: Nullable<string>;
  expenseSortField?: ExpenseSortField | null;
  expenseSortDirection?: SortDirection | null;
  deletedAt?: Nullable<ISODateString>;
  createdAt: ISODateString;
  updatedAt: ISODateString;
  /**
   * The job's derived billing position, computed server-side from the payment
   * ledger. Present only on responses that pay for the aggregate: the job list
   * and the client detail screen.
   *
   * Money is in dollars. `contractValue` is null when the job has no active
   * breakdown document: completeness is then unknowable, and the billing bar
   * renders an empty track rather than inventing a denominator.
   *
   * NOT named `billingState`: that is already this type's billing ADDRESS
   * state, the US state beside billingCity and billingZip. Two unrelated
   * meanings on one field would be a defect waiting to happen.
   */
  jobBillingSummary?: {
    state: JobBillingState;
    contractValue: Nullable<number>;
    invoiced: number;
    collected: number;
  };
}

export interface JobDetail extends Job {
  client: Client;
  location: Location;
  manager: Nullable<Manager>;
  /**
   * Photos on this job, excluding the app-owned Expenses folder and its
   * subfolders. Present only on the list response — the single-job endpoints
   * that also return JobDetail do not compute it, which is why it is optional.
   */
  photoCount?: number;
}

export type JobInput = {
  clientId: UUID;
  locationId: UUID;
  managerId?: Nullable<UUID>;
  projectType: string;
  customProjectType?: string;
  description?: string;
  serviceAddressNote?: string;
  billingAddressLabel?: string;
  billingAddressLine1?: string;
  billingAddressLine2?: string;
  billingCity?: string;
  billingState?: string;
  billingZip?: string;
  disposition?: JobDisposition;
  /** @deprecated Replaced by jobCardStatus + invoiceStatus. */
  billingStatus?: BillingStatus;
  jobCardStatus?: JobCardStatus;
  invoiceStatus?: JobInvoiceStatus;
  invoicePaidDate?: ISODate | null;
  noticeDate?: ISODate;
  estimateSendDate?: ISODate;
  startDate?: ISODate;
  completedDate?: ISODate;
  onedrivePath?: string;
  jobCardPath?: string;
  invoiceType?: InvoiceType;
  invoiceNumber?: string;
  /** @deprecated Edit UI removed. */
  billingType?: BillingType;
  /** @deprecated Edit UI removed. */
  billingRate?: string;
  lastInvoicedDate?: ISODate;
  highlightColor?: string | null;
  expenseSortField?: ExpenseSortField | null;
  expenseSortDirection?: SortDirection | null;
};

export type JobUpdateInput = Partial<JobInput>;

export type BreakdownSlotKey = 'generic' | 'carpet';

export function projectTypeToSlotKey(
  projectType: string | null | undefined,
): BreakdownSlotKey {
  return projectType === 'Carpet Cleaning' ? 'carpet' : 'generic';
}
